import { createDashboardSupabaseClient } from "@/lib/auth/supabase";
import type { Session } from "@supabase/supabase-js";
import { browserJson, browserAuthOperation, createBrowserOperation, retireBrowserAuth, isBrowserAuthActive, signOutBrowserAuth } from "@/lib/auth/operation";
import { signOutMessage } from "@/features/auth/sign-out-feedback";
import qrcode from "qrcode-generator";
import { dashboardAuthRedirectUrl } from "@/features/auth/dashboard-auth-navigation";
import { takeDashboardEmailCallback } from "@/features/auth/dashboard-email-callback";

import {
  parseDashboardQrPoll,
  parseDashboardQrStart,
  requireDashboardQrTarget,
} from "@/features/auth/dashboard-login-model";
import type { DashboardQrStart } from "@/features/auth/dashboard-login-model";
import {
  localizeDashboardLoginDocument,
  readDashboardLoginLanguage,
  translateDashboardLoginCopy,
  translateDashboardLoginStatus,
} from "@/features/auth/dashboard-login-localization";
import { setLoginMood, startLoginAmbience } from "@/features/auth/login-ambience";

const dashboardLoginLanguage = readDashboardLoginLanguage();
const loginT = (source: string) => translateDashboardLoginCopy(source, undefined, dashboardLoginLanguage);
localizeDashboardLoginDocument(dashboardLoginLanguage);
startLoginAmbience();

const authControls = element<HTMLElement>("#auth-controls");
const emailInput = element<HTMLInputElement>("#email-input");
const magicForm = element<HTMLFormElement>("#magic-link-form");
const magicStatus = element<HTMLElement>("#magic-status");
const magicSubmit = element<HTMLButtonElement>("#magic-submit");
const qrCanvas = element<HTMLCanvasElement>("#qr-canvas");
const qrCountdown = element<HTMLElement>("#qr-countdown");
const qrExpiry = element<HTMLElement>("#qr-expiry");
const qrProgressRing = element<HTMLElement>("#qr-progress-ring");
const refreshQrButton = element<HTMLButtonElement>("#refresh-qr-button");
const sessionCard = element<HTMLElement>("#session-card");
const sessionEmail = element<HTMLElement>("#session-email");
const signOutButton = element<HTMLButtonElement>("#sign-out-button");
const formPane = element<HTMLElement>(".auth-form-pane");
const sentPanel = element<HTMLElement>("#sent-panel");
const sentHeading = element<HTMLElement>("#sent-heading");
const sentEmail = element<HTMLElement>("#sent-email");
const sentResend = element<HTMLButtonElement>("#sent-resend");
const sentChange = element<HTMLButtonElement>("#sent-change");


let supabase: ReturnType<typeof createDashboardSupabaseClient> | null = null;
let qrStart: DashboardQrStart | null = null;
let qrCountdownTimer = 0;
let qrExpiryTimer = 0;
let qrPollTimer = 0;
const lifetime = new AbortController();
let qrLifetime = new AbortController();
let initializing = false;
let sending = false;
let installing = false;
let loggingOut = false;
let logoutPending = false;
let logoutFeedback: string | null = null;
let activeSession: Session | null = null;
let logoutClient: ReturnType<typeof createDashboardSupabaseClient> | null = null;
let startupOperation: ReturnType<typeof createBrowserOperation> | null = null;
let rejectedCallback = false;
const callbackRecoveryMessage = "This sign-in link could not be used. Request a new email or use the QR code.";
let unsubscribe: (() => void) | undefined;
const qrLogo = new Image();
let qrLogoRedrawPayload: string | null = null;

qrLogo.decoding = "async";
qrLogo.src = "/assets/logo.png";
qrLogo.addEventListener("load", () => {
  if (qrLogoRedrawPayload) {
    const payload = qrLogoRedrawPayload;
    qrLogoRedrawPayload = null;
    drawQr(payload);
  }
});

bindUi();
void init();

async function init() {
  if (loggingOut || initializing || lifetime.signal.aborted) return;
  initializing = true;
  rejectedCallback = false;
  stopQrActivity();
  unsubscribe?.();
  retireBrowserAuth(supabase);
  supabase = null;
  magicSubmit.disabled = true;
  refreshQrButton.disabled = true;
  const operation = createBrowserOperation(20_000, lifetime.signal);
  startupOperation = operation;
  setStatus("info", "Checking...");
  setLoginMood("checking");

  try {
    const config = await operation.wait(loadAuthConfig(operation.signal));
    operation.assert();
    unsubscribe?.();
    retireBrowserAuth(supabase);
    const callback = takeDashboardEmailCallback();
    // The standalone page owns callback consumption. SDK auto-detection both
    // caches rejected initialization and may leave failed auth parameters behind.
    // A restored account's refresh must not delete the verifier of a fresh
    // email requested here. The authenticated dashboard owns token refresh.
    supabase = createDashboardSupabaseClient(config, { detectSessionInUrl: false, autoRefreshToken: false });

    const { error: initializationError } = await operation.wait(browserAuthOperation(supabase, () => supabase!.auth.initialize(), operation.signal));
    if (initializationError) throw initializationError;

    if (callback.kind !== "none") {
      try {
        if (callback.kind === "rejected") throw new Error(callbackRecoveryMessage);
        const result = await operation.wait(browserAuthOperation(supabase, () => supabase!.auth.exchangeCodeForSession(callback.code), operation.signal));
        if (result.error || !result.data.session) throw result.error ?? new Error(callbackRecoveryMessage);
        setSession(result.data.session);
        return;
      } catch {
        // Retire before allowing another attempt: an ignored abort may still
        // settle later, but cannot remove a new verifier or install a session.
        retireBrowserAuth(supabase);
        operation.assert();
        rejectedCallback = true;
        supabase = createDashboardSupabaseClient(config, { detectSessionInUrl: false, autoRefreshToken: false });
        const result = await operation.wait(browserAuthOperation(supabase, () => supabase!.auth.initialize(), operation.signal));
        if (result.error) throw result.error;
      }
    }

    const { data, error } = rejectedCallback ? { data: { session: null }, error: null }
      : await operation.wait(browserAuthOperation(supabase, () => supabase!.auth.getSession(), operation.signal));
    // auth-js removes the saved session before returning a rejected refresh.
    // This is a normal signed-out state, not a failure of the sign-in page.
    // Keep transport failures and unrelated auth errors on the recovery path.
    if (error && error.code !== "refresh_token_not_found" && error.code !== "refresh_token_already_used") {
      throw error;
    }
    // A rejected link must not masquerade as a successful sign-in to a restored
    // different account. Preserve that stored session, but keep fresh options visible.
    setSession(rejectedCallback ? null : data.session ?? null);
    if (!rejectedCallback) {
      const subscription = supabase.auth.onAuthStateChange((_event, session) => { if (!lifetime.signal.aborted && !sending && !installing) setSession(session); });
      unsubscribe = () => subscription?.data.subscription.unsubscribe();
    }
    magicSubmit.disabled = false;
    clearStatus();
  } catch (error) {
    if (loggingOut) return;
    retireBrowserAuth(supabase);
    supabase = null;
    if (lifetime.signal.aborted) return;
    setStatus("warning", messageFor(error));
    magicSubmit.disabled = false;
    refreshQrButton.disabled = true;
  } finally {
    operation.finish();
    initializing = false;
  }
}

function bindUi() {
  refreshQrButton.addEventListener("click", () => void refreshQrChallenge());
  signOutButton.addEventListener("click", () => void performSignOut());
  magicForm.addEventListener("submit", (event) => void submitMagicLink(event));
  sentResend.addEventListener("click", () => magicForm.requestSubmit());
  sentChange.addEventListener("click", () => {
    hideSentPanel();
    clearStatus();
    emailInput.focus();
    emailInput.select();
  });
  const stop = () => { lifetime.abort(); stopQrActivity(); unsubscribe?.(); retireBrowserAuth(supabase); };
  window.addEventListener("beforeunload", stop, { once: true });
  window.addEventListener("pagehide", stop, { once: true });
  window.addEventListener("pageshow", (event) => { if (event.persisted) window.location.reload(); });
}

async function performSignOut() {
  if (logoutPending || lifetime.signal.aborted) return;
  logoutClient ??= supabase;
  if (!logoutClient) return;
  loggingOut = true;
  logoutPending = true;
  startupOperation?.cancel();
  stopQrActivity();
  unsubscribe?.();
  signOutButton.disabled = magicSubmit.disabled = refreshQrButton.disabled = true;
  logoutFeedback = "Signing out…";
  setStatus("info", logoutFeedback);
  const result = await signOutBrowserAuth(logoutClient, installing ? undefined : activeSession?.access_token, lifetime.signal);
  logoutPending = false;
  if (lifetime.signal.aborted) return;
  activeSession = null;
  supabase = null;
  hideSentPanel();
  authControls.hidden = false;
  sessionCard.dataset.active = "false";
  logoutFeedback = signOutMessage(result, loginT);
  setStatus("warning", logoutFeedback);
  magicSubmit.disabled = false;
  if (result.local) { loggingOut = false; logoutClient = null; }
}

async function submitMagicLink(event: SubmitEvent) {
  event.preventDefault();
  if (sending || initializing || lifetime.signal.aborted) return;
  if (loggingOut) { await performSignOut(); return; }

  const email = emailInput.value.trim().toLowerCase();
  if (!email) {
    setStatus("warning", "Enter your email.");
    return;
  }

  if (!supabase || !isBrowserAuthActive(supabase)) {
    await init();
    if (!supabase || activeSession || lifetime.signal.aborted) return;
  }

  magicSubmit.disabled = true;
  sending = true;
  stopQrActivity();
  setStatus("info", "Sending...");
  setLoginMood("sending");
  const redirectTo = authRedirectUrl();
  try {
  const { error } = await browserAuthOperation(supabase, () => supabase!.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: redirectTo,
      shouldCreateUser: false,
    },
  }), lifetime.signal);

  if (error) {
    throw error;
  }
  if (lifetime.signal.aborted) return;
  setStatus("success", "Sign-in link sent. Open the email from ArtCrew and press “Sign in to ArtCrew”.");
  setLoginMood("sent");
  showSentPanel(email);
  } catch {
    if (!loggingOut && !lifetime.signal.aborted) {
      setStatus("warning", "We could not confirm email delivery. Check your inbox for the latest email, or try again.");
      retireBrowserAuth(supabase);
      supabase = null;
    }
  } finally {
    sending = false;
    if (!loggingOut && !lifetime.signal.aborted) magicSubmit.disabled = false;
  }
}

function authRedirectUrl(): string {
  return dashboardAuthRedirectUrl(window.location.origin);
}

async function loadAuthConfig(signal: AbortSignal): Promise<{ supabaseKey: string; supabaseUrl: string }> {
  const { response, data: raw } = await browserJson("/api/dashboard-auth/config", {
    signal,
    cache: "no-store",
    credentials: "same-origin",
    headers: { Accept: "application/json" },
  });
  const data = recordValue(raw) ?? {};
  if (!response.ok || typeof data.supabaseUrl !== "string" || typeof data.supabaseKey !== "string") {
    throw new Error(typeof data.error === "string" ? data.error : "Dashboard auth is not configured.");
  }
  const origin = new URL(data.supabaseUrl).origin;
  if (!origin.startsWith("https://") || !origin.endsWith(".supabase.co")) {
    throw new Error("Dashboard auth returned an invalid service origin.");
  }
  return { supabaseKey: data.supabaseKey, supabaseUrl: origin };
}

function setSession(session: Session | null) {
  if (loggingOut) return;
  activeSession = session;
  const active = Boolean(session);
  if (active) {
    stopQrActivity();
    setLoginMood("entering");
    window.location.replace("/dashboard/app");
    return;
  }
  sessionCard.dataset.active = "false";
  authControls.hidden = false;
  sessionEmail.textContent = loginT("Dashboard session inactive.");
  if (supabase && !qrStart) {
    void refreshQrChallenge();
  }
}

async function refreshQrChallenge() {
  if (loggingOut || lifetime.signal.aborted || sending) return;
  stopQrActivity();
  qrLifetime = new AbortController();
  const signal = qrLifetime.signal;
  qrStart = null;
  refreshQrButton.disabled = true;
  refreshQrButton.hidden = true;
  qrExpiry.hidden = true;
  try {
    const { response, data } = await browserJson("/api/dashboard-auth/qr/start", {
      signal,
      cache: "no-store",
      credentials: "same-origin",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      method: "POST",
      body: "{}",
    });
    if (!response.ok) {
      throw new Error(errorFrom(data, "Could not create QR login."));
    }
    qrStart = parseDashboardQrStart(data);
    drawQr(qrStart.payload);
    setStatus("info", "Scan with the ArtCrew app, then approve this browser.");
    startQrExpiryTracking(qrStart);
    pollQrChallenge();
  } catch (error) {
    if (signal.aborted || lifetime.signal.aborted) return;
    setStatus("warning", messageFor(error));
    refreshQrButton.hidden = false;
  } finally {
    if (!signal.aborted && !lifetime.signal.aborted) refreshQrButton.disabled = false;
  }
}

async function pollQrChallenge() {
  const current = qrStart;
  const signal = qrLifetime.signal;
  if (!current || !supabase) {
    return;
  }
  if (Date.parse(current.challenge.expiresAt) <= Date.now()) {
    renewExpiredQrChallenge(current);
    return;
  }

  try {
    const response = await jsonPost("/api/dashboard-auth/qr/poll", {
      challengeId: current.challenge.challengeId,
      pollSecret: current.pollSecret,
    }, signal);
    if (qrStart !== current || signal.aborted || lifetime.signal.aborted) return;
    if (!response.ok) {
      throw new Error(errorFrom(response.data, "Could not check QR login."));
    }
    const poll = parseDashboardQrPoll(response.data);
    if (poll.status === "approved") {
      stopQrExpiryTracking();
      qrExpiry.hidden = true;
      setStatus("success", poll.approvedAccount
        ? `Approved for ${poll.approvedAccount}. Signing in...`
        : "Approved. Signing in...");
      setLoginMood("approved");
      await redeemQrChallenge(current);
      return;
    }
    if (poll.status === "expired") {
      renewExpiredQrChallenge(current);
      return;
    }
  } catch (error) {
    if (signal.aborted || lifetime.signal.aborted) return;
    setStatus("warning", messageFor(error));
    stopQrActivity();
    qrStart = null;
    refreshQrButton.hidden = false;
    refreshQrButton.disabled = false;
    if (supabase && !isBrowserAuthActive(supabase)) {
      supabase = null;
      refreshQrButton.disabled = true;
    }
  }

  if (qrStart === current) {
    qrPollTimer = window.setTimeout(() => void pollQrChallenge(), 1_800);
  }
}

async function redeemQrChallenge(current: DashboardQrStart) {
  const signal = qrLifetime.signal;
  if (!supabase || qrStart !== current) {
    return;
  }
  stopQrExpiryTracking();
  qrStart = null;
  const response = await jsonPost("/api/dashboard-auth/qr/redeem", {
    challengeId: current.challenge.challengeId,
    pollSecret: current.pollSecret,
  }, signal);
  if (signal.aborted || lifetime.signal.aborted) return;
  if (!response.ok) {
    throw new Error(errorFrom(response.data, "Could not finish QR login."));
  }

  const data = recordValue(response.data);
  const session = recordValue(data?.session);
  const accessToken = stringValue(session?.access_token);
  const refreshToken = stringValue(session?.refresh_token);
  requireDashboardQrTarget(data?.target);
  if (data?.status !== "approved" || !accessToken || !refreshToken) {
    throw new Error("Dashboard QR login returned an invalid session.");
  }

  installing = true;
  try {
  const { error } = await browserAuthOperation(supabase, () => supabase!.auth.setSession({
    access_token: accessToken,
    refresh_token: refreshToken,
  }), signal);
  if (error) {
    throw error;
  }
  if (!signal.aborted && !lifetime.signal.aborted) window.location.replace("/dashboard/app");
  } finally { installing = false; }
}

async function jsonPost(url: string, body: Record<string, unknown>, signal: AbortSignal) {
  const { response, data } = await browserJson(url, {
    signal,
    body: JSON.stringify(body),
    cache: "no-store",
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    method: "POST",
  });
  return {
    data,
    ok: response.ok,
  };
}

function drawQr(payload: string) {
  const qr = qrcode(0, "L");
  qr.addData(payload);
  qr.make();

  const cssSize = 192;
  const pixelRatio = Math.min(Math.max(window.devicePixelRatio || 1, 1), 2);
  const renderSize = Math.round(cssSize * pixelRatio);
  if (qrCanvas.width !== renderSize || qrCanvas.height !== renderSize) {
    qrCanvas.width = renderSize;
    qrCanvas.height = renderSize;
  }

  const context = qrCanvas.getContext("2d");
  if (!context) {
    throw new Error("QR rendering is unavailable in this browser.");
  }
  const modules = qr.getModuleCount();
  const quietZone = 4;
  const cell = renderSize / (modules + quietZone * 2);
  const inset = quietZone * cell;

  context.fillStyle = "#f7f7f8";
  context.fillRect(0, 0, renderSize, renderSize);
  context.fillStyle = "#171717";
  for (let row = 0; row < modules; row += 1) {
    for (let column = 0; column < modules; column += 1) {
      if (qr.isDark(row, column) && !isFinderModule(row, column, modules)) {
        const dotSize = cell * 0.9;
        fillRoundedRect(
          context,
          inset + column * cell + (cell - dotSize) / 2,
          inset + row * cell + (cell - dotSize) / 2,
          dotSize,
          dotSize,
          dotSize * 0.34,
        );
      }
    }
  }

  drawFinder(context, inset, inset, cell);
  drawFinder(context, inset + (modules - 7) * cell, inset, cell);
  drawFinder(context, inset, inset + (modules - 7) * cell, cell);
  drawQrLogo(context, renderSize, cell, payload);
}

function isFinderModule(row: number, column: number, modules: number): boolean {
  const inTop = row < 7;
  const inLeft = column < 7;
  const inRight = column >= modules - 7;
  const inBottom = row >= modules - 7;
  return (inTop && (inLeft || inRight)) || (inBottom && inLeft);
}

function drawFinder(context: CanvasRenderingContext2D, x: number, y: number, cell: number) {
  context.fillStyle = "#171717";
  fillRoundedRect(context, x, y, cell * 7, cell * 7, cell * 1.65);
  context.fillStyle = "#f7f7f8";
  fillRoundedRect(context, x + cell, y + cell, cell * 5, cell * 5, cell * 1.15);
  context.fillStyle = "#171717";
  fillRoundedRect(context, x + cell * 2, y + cell * 2, cell * 3, cell * 3, cell * 0.8);
}

function drawQrLogo(
  context: CanvasRenderingContext2D,
  renderSize: number,
  cell: number,
  payload: string,
) {
  const plateSize = cell * 10;
  const plateX = (renderSize - plateSize) / 2;
  const plateY = (renderSize - plateSize) / 2;
  context.fillStyle = "#f7f7f8";
  fillRoundedRect(context, plateX, plateY, plateSize, plateSize, cell * 2.4);

  const badgeInset = cell * 1.15;
  const badgeSize = plateSize - badgeInset * 2;
  context.fillStyle = "#7c3aed";
  fillRoundedRect(
    context,
    plateX + badgeInset,
    plateY + badgeInset,
    badgeSize,
    badgeSize,
    cell * 1.7,
  );

  if (!qrLogo.complete || qrLogo.naturalWidth === 0) {
    qrLogoRedrawPayload = payload;
    return;
  }

  const logoHeight = badgeSize * 0.56;
  const logoWidth = logoHeight * (qrLogo.naturalWidth / qrLogo.naturalHeight);
  context.drawImage(
    qrLogo,
    (renderSize - logoWidth) / 2,
    (renderSize - logoHeight) / 2,
    logoWidth,
    logoHeight,
  );
}

function fillRoundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  const safeRadius = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + safeRadius, y);
  context.lineTo(x + width - safeRadius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
  context.lineTo(x + width, y + height - safeRadius);
  context.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
  context.lineTo(x + safeRadius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
  context.lineTo(x, y + safeRadius);
  context.quadraticCurveTo(x, y, x + safeRadius, y);
  context.closePath();
  context.fill();
}

function stopQrPolling() {
  window.clearTimeout(qrPollTimer);
  qrPollTimer = 0;
}

function startQrExpiryTracking(current: DashboardQrStart) {
  stopQrExpiryTracking();
  const expiresAt = Date.parse(current.challenge.expiresAt);
  const durationMs = Math.max(1_000, expiresAt - Date.now());
  qrExpiry.hidden = false;

  const updateCountdown = () => {
    if (qrStart !== current) {
      return;
    }
    const remainingMs = Math.max(0, expiresAt - Date.now());
    const remainingSeconds = Math.ceil(remainingMs / 1_000);
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = String(remainingSeconds % 60).padStart(2, "0");
    const progress = Math.max(0, Math.min(1, remainingMs / durationMs));
    qrCountdown.textContent = `${minutes}:${seconds}`;
    qrProgressRing.style.setProperty("--qr-progress", `${progress * 360}deg`);
    qrExpiry.setAttribute(
      "aria-label",
      loginT(`QR code refreshes automatically in ${remainingSeconds} second${remainingSeconds === 1 ? "" : "s"}.`),
    );
  };

  updateCountdown();
  qrCountdownTimer = window.setInterval(updateCountdown, 1_000);
  qrExpiryTimer = window.setTimeout(
    () => renewExpiredQrChallenge(current),
    Math.max(0, expiresAt - Date.now()),
  );
}

function renewExpiredQrChallenge(current: DashboardQrStart) {
  if (qrStart !== current) {
    return;
  }
  setStatus("info", "Refreshing QR code...");
  void refreshQrChallenge();
}

function stopQrExpiryTracking() {
  window.clearInterval(qrCountdownTimer);
  window.clearTimeout(qrExpiryTimer);
  qrCountdownTimer = 0;
  qrExpiryTimer = 0;
}

function stopQrActivity() {
  qrLifetime.abort();
  qrStart = null;
  qrExpiry.hidden = true;
  refreshQrButton.hidden = false;
  stopQrPolling();
  stopQrExpiryTracking();
}

function setStatus(kind: "info" | "success" | "warning", message: string) {
  if (loggingOut && message !== logoutFeedback) return;
  if (rejectedCallback && message === "Scan with the ArtCrew app, then approve this browser.") {
    kind = "warning";
    message = callbackRecoveryMessage;
  }
  magicStatus.dataset.kind = kind;
  magicStatus.textContent = translateDashboardLoginStatus(message, dashboardLoginLanguage);
  if (kind === "warning") setLoginMood("warning");
}

function clearStatus() {
  if (loggingOut) return;
  if (rejectedCallback) { setStatus("warning", callbackRecoveryMessage); return; }
  magicStatus.removeAttribute("data-kind");
  magicStatus.textContent = "";
  setLoginMood("idle");
}

function showSentPanel(email: string) {
  const alreadyShown = formPane.dataset.sent === "true";
  sentEmail.textContent = email;
  formPane.dataset.sent = "true";
  sentPanel.hidden = false;
  if (!alreadyShown) sentHeading.focus();
}

function hideSentPanel() {
  delete formPane.dataset.sent;
  sentPanel.hidden = true;
}

function element<T extends Element>(selector: string): T {
  const value = document.querySelector<T>(selector);
  if (!value) {
    throw new Error(`Dashboard login is missing ${selector}.`);
  }
  return value;
}

function errorFrom(value: unknown, fallback: string): string {
  const error = recordValue(value)?.error;
  return typeof error === "string" && error.length <= 240 ? error : fallback;
}

function messageFor(error: unknown): string {
  return error instanceof Error ? error.message : "Unexpected dashboard auth error.";
}

function recordValue(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}
